import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@repo/api-client";

interface DonationChannel {
  id: string;
  region: string;
  label: string;
  method: string;
  handle: string;
  note?: string;
  active: boolean;
}

export function DonationInfoPage() {
  const [copied, setCopied] = useState<string | null>(null);
  const [channels, setChannels] = useState<DonationChannel[]>([]);

  useEffect(() => {
    api.listDonationChannels().then((res) => setChannels(res.channels)).catch(() => {});
  }, []);

  const usChannels = channels.filter((c) => c.region === "US");
  const indiaChannels = channels.filter((c) => c.region === "India");

  function copyToClipboard(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div className="donation-info-page">
      <div className="container">
        {/* Header */}
        <div className="checkin-header">
          <div>
            <span className="eyebrow">AekamPass · Donation Center</span>
            <h1 className="display-2">Support Aekam</h1>
            <p className="body-lg text-muted" style={{ maxWidth: "600px" }}>
              Aekam is a community-supported platform. Your donation
              helps us spread mindful meditation, vegetarianism, and inner self-awareness
              globally — at no cost to seekers.
            </p>
          </div>
          <Link to="/ops" className="btn btn--outline btn--sm">← Back to Ops</Link>
        </div>

        {/* Mission block */}
        <section className="donation-mission-block">
          <blockquote className="donation-mission-quote">
            "Our sole mission is to spread mindful meditation, vegetarianism, and to make
            everyone aware of their inner self to enlighten themselves."
          </blockquote>
          <p className="donation-mission-org">
            Community-supported spiritual platform.
          </p>
        </section>

        {/* Donation cards */}
        <div className="donation-cards-grid">
          {/* USA */}
          <div className="donation-card">
            <div className="donation-card__flag">🇺🇸</div>
            <h2 className="donation-card__title">Donate from USA</h2>

            <div className="donation-detail-group">
              <div className="donation-detail-label">Bank Transfer</div>
              <div className="donation-detail-row">
                <span className="donation-detail-key">Bank</span>
                <span className="donation-detail-value">Wells Fargo</span>
              </div>
              <div className="donation-detail-row">
                <span className="donation-detail-key">Account #</span>
                <button
                  type="button"
                  className="donation-copy-btn"
                  onClick={() => copyToClipboard("3017957717", "acct")}
                  title="Copy account number"
                >
                  <span className="donation-detail-value">3017957717</span>
                  <span className="donation-copy-icon">
                    {copied === "acct" ? "✓" : "📋"}
                  </span>
                </button>
              </div>
            </div>

            <div className="donation-detail-group">
              <div className="donation-detail-label">Zelle</div>
              <div className="donation-detail-row">
                <span className="donation-detail-key">Send to</span>
                <button
                  type="button"
                  className="donation-copy-btn"
                onClick={() => copyToClipboard("hello@aekam.app", "zelle")}
                  title="Copy Zelle address"
                >
                <span className="donation-detail-value">hello@aekam.app</span>
                  <span className="donation-copy-icon">
                    {copied === "zelle" ? "✓" : "📋"}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* India */}
          <div className="donation-card">
            <div className="donation-card__flag">🇮🇳</div>
            <h2 className="donation-card__title">Donate from India</h2>

            <div className="donation-detail-group">
              <div className="donation-detail-label">UPI Payment</div>
              <div className="donation-detail-row">
                <span className="donation-detail-key">UPI ID</span>
                <button
                  type="button"
                  className="donation-copy-btn"
                  onClick={() => copyToClipboard("Wayto97046356@barodampay", "upi")}
                  title="Copy UPI ID"
                >
                  <span className="donation-detail-value">Wayto97046356@barodampay</span>
                  <span className="donation-copy-icon">
                    {copied === "upi" ? "✓" : "📋"}
                  </span>
                </button>
              </div>
            </div>

            <div className="donation-detail-group">
              <div className="donation-detail-label">UPI QR Code</div>
              <div className="donation-qr-placeholder">
                <span>📱</span>
                <p>Scan with any UPI app</p>
                <p className="text-muted" style={{ fontSize: "0.8rem" }}>
                  GPay · PhonePe · Paytm · BHIM
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tax info */}
        <div className="donation-tax-note">
          <span className="donation-tax-icon">📄</span>
          <div>
            <strong>Support the Platform</strong>
            <p>
              Aekam is supported by its community. A receipt will be emailed
              upon request — contact{" "}
              <a href="mailto:hello@aekam.app" className="text-link">
                hello@aekam.app
              </a>
            </p>
          </div>
        </div>

        {/* Contact */}
        <div className="donation-contact">
          <h3>Questions about donating?</h3>
          <p className="text-muted">
            Email us at{" "}
            <a href="mailto:hello@aekam.app" className="text-link">
              hello@aekam.app
            </a>{" "}
            or call{" "}
            <a href="tel:+19292665742" className="text-link">
              929-266-5742
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
